import React, { useState } from "react";
import { useOidcAccessToken } from "@axa-fr/react-oidc";
import "../../assets/scss/pages/homepage.scss";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppContext } from "../../AppProvider.jsx";
import { useTypes } from "../../utilities/types.js";
import moment from "moment";
import { DatePickerFilter } from "../../Components/common/List/Filters/DatePickerFilter.jsx";
import ChoroplethMapOfItalianRegions from "../KPI/Shipments/ChoroplethMapOfItalianRegions.jsx";
import ShipmentsByCarrier from "../KPI/Shipments/ShipmentsByCarrier.tsx";
import ShipmentsByStatus from "../KPI/Shipments/ShipmentsByStatus.tsx";
import { ShipmentsStatisticsProvider } from "../KPI/providers/ShipmentsStatistics.tsx";
import { ShipmentsPerformancesProvider } from "../KPI/providers/ShipmentsPerformances.tsx";
import AverageDeliveryTimeByCarrier from "../KPI/Shipments/AverageDeliveryTimeByCarrier.tsx";
import ShipmentsByCOD from "../KPI/Shipments/ShipmentsByCOD.tsx";
import { ReportCard } from "../KPI/ReportCard.tsx";
import {
  faTruckFast,
  faBagsShopping,
  faHandHoldingBox,
} from "../../utilities/fontawesomeIcons.js";
import { ShipmentsProvider } from "../KPI/providers/Shipments.tsx";
import AverageFirstDeliveryAttemptTimeByCarrier from "../KPI/Shipments/AverageFirstDeliveryAttemptTimeByCarrier.tsx";
import { ShipmentsStatisticsAggregationDailyProvider } from "../KPI/providers/ShipmentsStatisticsAggregationDaily.tsx";
import ShipmentsPerDay from "../KPI/Shipments/ShipmentsPerDay.tsx";
import { ShipmentsPerformancesOnTimeProvider } from "../KPI/providers/ShipmentsPerformancesOnTime.tsx";
import { ShipmentsPerformancesFirstAttemptProvider } from "../KPI/providers/ShipmentsPerformancesFirstAttempt.tsx";
import OnContractTime from "../KPI/Shipments/OnContractTime.tsx";
import { ShipmentsPerformancesFirstAttemptDeliveredProvider } from "../KPI/providers/ShipmentsPerformancesFirstAttemptDelivered.tsx";
import DeliveredOnTheFirstTry from "../KPI/Shipments/DeliveredOnTheFirstTry.tsx";
import { ShipmentsPerformancesDeliveryDaysGroupedProvider } from "../KPI/providers/ShipmentsPerformancesDeliveryDaysGrouped.tsx";
import DeliveryDaysGroupedByCarrier from "../KPI/Shipments/DeliveryDaysGroupedByCarrier.tsx";
import { ShipmentsPerformancesDelayedDaysGroupedProvider } from "../KPI/providers/ShipmentsPerformancesDelayedDaysGrouped.tsx";
import DelayedDaysGroupedByCarrier from "../KPI/Shipments/DelayedDaysGroupedByCarrier.tsx";

const Dashboard = () => {
  const { accessTokenPayload } = useOidcAccessToken();
  const [dateRangeForMyShipments, setDateRangeForMyShipments] = useState([
    moment().subtract(1, "months").startOf("day").toDate(),
    moment().endOf("day").toDate(),
  ]);
  const { dictionary, owner } = useAppContext();
  const { confirmationBoolOptions } = useTypes();

  return (
    <React.Fragment>
      <div className="homepage-header">
        <h1 className="title">
          {dictionary.words.welcome_back} {accessTokenPayload.name}!
        </h1>
        <span className="role">{owner?.name}</span>
        <div className="rapid-links d-none d-xl-flex">
          <Link to={"/shipments"} className="link">
            <FontAwesomeIcon icon={faTruckFast} size="2x" />
            <span>{dictionary.shipments.my_shipments.toUpperCase()}</span>
          </Link>
          <Link to={"/orders"} className="link">
            <FontAwesomeIcon icon={faBagsShopping} size="2x" />
            <span>{dictionary.orders.my_orders.toUpperCase()}</span>
          </Link>
          <Link to={"/collections"} className="link">
            <FontAwesomeIcon icon={faHandHoldingBox} size="2x" />
            <span>{dictionary.collections.my_collections.toUpperCase()}</span>
          </Link>
        </div>
      </div>
      <section className="mt-4">
        <div className="row">
          <div className="col">
            <h2>{dictionary.words.overview}</h2>
          </div>
        </div>
        <div className="row gx-2">
          <div className="col-12 col-xs-6 col-sm-6 col-md-4 col-lg-3 mb-2">
            <ShipmentsStatisticsProvider
              filters={{
                start: moment().startOf("day").format(),
                end: moment().endOf("day").format(),
              }}
            >
              <ReportCard
                title={dictionary.shipments.my_shipments}
                subtitle={dictionary.times.today}
                path={"/shipments"}
              />
            </ShipmentsStatisticsProvider>
          </div>
          <div className="col-12 col-xs-6 col-sm-6 col-md-4 col-lg-3 mb-2">
            <ShipmentsStatisticsProvider
              filters={{
                start: moment().subtract(1, "months").startOf("day").format(),
                end: moment().endOf("day").format(),
              }}
            >
              <ReportCard
                path={"/shipments"}
                title={dictionary.shipments.my_shipments}
                subtitle={dictionary.times.last_30_days}
              />
            </ShipmentsStatisticsProvider>
          </div>
          <div className="col-12 col-xs-6 col-sm-6 col-md-4 col-lg-3 mb-2">
            <ShipmentsProvider
              filters={{
                start: moment().subtract(1, "months").startOf("day").format(),
                end: moment().endOf("day").format(),
              }}
            >
              <ReportCard
                provider="shipments"
                path={"/shipments"}
                title={dictionary.shipments.shipments_in_stock}
                subtitle={dictionary.times.last_60_days}
                additionalFilters={{
                  stockReleaseCondition: confirmationBoolOptions.find(
                    (x) => x.value === "true"
                  ),
                }}
              />
            </ShipmentsProvider>
          </div>
        </div>
      </section>
      <section className="my-3">
        <div className="row">
          <div className="col-8">
            <h2>{dictionary.shipments.my_shipments}</h2>
          </div>
          <div className="col">
            <DatePickerFilter
              filter={{}}
              value={dateRangeForMyShipments}
              onChange={(fieldName: string, dates: []) => {
                setDateRangeForMyShipments(dates);
              }}
            ></DatePickerFilter>
          </div>
        </div>
        <div className="row gx-2 mb-2">
          <div className="col-12 col-lg-7 mb-2">
            <ShipmentsStatisticsAggregationDailyProvider
              filters={{
                start: moment(dateRangeForMyShipments[0])
                  .startOf("day")
                  .format(),
                end: moment(dateRangeForMyShipments[1]).endOf("day").format(),
              }}
            >
              <ShipmentsPerDay />
            </ShipmentsStatisticsAggregationDailyProvider>
          </div>
          <div className="col-12 col-lg-5 mb-2">
            <ShipmentsStatisticsProvider
              filters={{
                start: moment(dateRangeForMyShipments[0])
                  .startOf("day")
                  .format(),
                end: moment(dateRangeForMyShipments[1]).endOf("day").format(),
              }}
            >
              <ChoroplethMapOfItalianRegions />
            </ShipmentsStatisticsProvider>
          </div>
        </div>
        <div className="row justify-content-center gx-2">
          <div className="col-12 col-xs-6 col-sm-6 col-md-4 col-lg-3 mb-2">
            <ShipmentsStatisticsProvider
              filters={{
                start: moment(dateRangeForMyShipments[0])
                  .startOf("day")
                  .format(),
                end: moment(dateRangeForMyShipments[1]).endOf("day").format(),
                statuses: [
                  "BOOKED",
                  "CONFIRMED",
                  "AVAILABLE",
                  "IN_TRANSIT",
                  "OUT_OF_DLV",
                  "EXCEPTION",
                  "FAILED_DLV",
                  "DELIVERED",
                  "INFO_RCVD",
                  "RET_SENDER",
                  "LOST",
                  "ABANDONED",
                ],
              }}
            >
              <ShipmentsByStatus />
            </ShipmentsStatisticsProvider>
          </div>
          <div className="col-12 col-xs-6 col-sm-6 col-md-4 col-lg-3 mb-2">
            <ShipmentsStatisticsProvider
              filters={{
                start: moment(dateRangeForMyShipments[0])
                  .startOf("day")
                  .format(),
                end: moment(dateRangeForMyShipments[1]).endOf("day").format(),
                statuses: [
                  "BOOKED",
                  "CONFIRMED",
                  "AVAILABLE",
                  "IN_TRANSIT",
                  "OUT_OF_DLV",
                  "EXCEPTION",
                  "FAILED_DLV",
                  "DELIVERED",
                  "INFO_RCVD",
                  "RET_SENDER",
                  "LOST",
                  "ABANDONED",
                ],
              }}
            >
              <ShipmentsByCarrier title={dictionary.kpi.used_carriers} />
            </ShipmentsStatisticsProvider>
          </div>
          <div className="col-12 col-xs-6 col-sm-6 col-md-4 col-lg-3 mb-2">
            <ShipmentsStatisticsProvider
              filters={{
                start: moment(dateRangeForMyShipments[0])
                  .startOf("day")
                  .format(),
                end: moment(dateRangeForMyShipments[1]).endOf("day").format(),
                statuses: [
                  "BOOKED",
                  "CONFIRMED",
                  "AVAILABLE",
                  "IN_TRANSIT",
                  "OUT_OF_DLV",
                  "EXCEPTION",
                  "FAILED_DLV",
                  "DELIVERED",
                  "INFO_RCVD",
                  "RET_SENDER",
                  "LOST",
                  "ABANDONED",
                ],
              }}
            >
              <ShipmentsByCOD />
            </ShipmentsStatisticsProvider>
          </div>
          <div className="col-12 col-xs-6 col-sm-6 col-md-4 col-lg-3 mb-2">
            <ShipmentsStatisticsProvider
              filters={{
                start: moment(dateRangeForMyShipments[0])
                  .startOf("day")
                  .format(),
                end: moment(dateRangeForMyShipments[1]).endOf("day").format(),
                statuses: ["RET_SENDER"],
              }}
            >
              <ShipmentsByCarrier title={dictionary.kpi.returned_shipments} />
            </ShipmentsStatisticsProvider>
          </div>
        </div>
        <div className="row gx-2">
          <div className="col-12 col-md-6 mb-2">
            <ShipmentsPerformancesProvider
              filters={{
                start: moment(dateRangeForMyShipments[0])
                  .startOf("day")
                  .format(),
                end: moment(dateRangeForMyShipments[1]).endOf("day").format(),
              }}
            >
              <AverageDeliveryTimeByCarrier />
            </ShipmentsPerformancesProvider>
          </div>
          <div className="col-12 col-md-6 mb-2">
            <ShipmentsPerformancesFirstAttemptProvider
              filters={{
                start: moment(dateRangeForMyShipments[0])
                  .startOf("day")
                  .format(),
                end: moment(dateRangeForMyShipments[1]).endOf("day").format(),
              }}
            >
              <AverageFirstDeliveryAttemptTimeByCarrier />
            </ShipmentsPerformancesFirstAttemptProvider>
          </div>
        </div>
        <div className="row gx-2">
          <div className="col-12 col-md-6 mb-2">
            <ShipmentsPerformancesOnTimeProvider
              filters={{
                start: moment(dateRangeForMyShipments[0])
                  .startOf("day")
                  .format(),
                end: moment(dateRangeForMyShipments[1]).endOf("day").format(),
              }}
            >
              <OnContractTime />
            </ShipmentsPerformancesOnTimeProvider>
          </div>
          <div className="col-12 col-md-6 mb-2">
            <ShipmentsPerformancesFirstAttemptDeliveredProvider
              filters={{
                start: moment(dateRangeForMyShipments[0])
                  .startOf("day")
                  .format(),
                end: moment(dateRangeForMyShipments[1]).endOf("day").format(),
              }}
            >
              <DeliveredOnTheFirstTry />
            </ShipmentsPerformancesFirstAttemptDeliveredProvider>
          </div>
        </div>
        <div className="row gx-2">
          <div className="col-12 col-lg-6 mb-2">
            <ShipmentsPerformancesDeliveryDaysGroupedProvider
              filters={{
                start: moment(dateRangeForMyShipments[0])
                  .startOf("day")
                  .format(),
                end: moment(dateRangeForMyShipments[1]).endOf("day").format(),
              }}
            >
              <DeliveryDaysGroupedByCarrier />
            </ShipmentsPerformancesDeliveryDaysGroupedProvider>
          </div>
          <div className="col-12 col-lg-6 mb-2">
            <ShipmentsPerformancesDelayedDaysGroupedProvider
              filters={{
                start: moment(dateRangeForMyShipments[0])
                  .startOf("day")
                  .format(),
                end: moment(dateRangeForMyShipments[1]).endOf("day").format(),
              }}
            >
              <DelayedDaysGroupedByCarrier />
            </ShipmentsPerformancesDelayedDaysGroupedProvider>
          </div>
        </div>
      </section>
    </React.Fragment>
  );
};

export default Dashboard;
